import { useCallback, useEffect, useMemo, useState } from 'react'

import { RenderConfig, stateToHTML } from 'draft-js-export-html'
import {
  ContentState,
  EditorState,
  RawDraftContentBlock,
  RawDraftContentState,
  convertFromRaw,
  convertToRaw
} from 'draft-js'
import api from '../services/api'
import { useLocation } from 'react-router-dom'
import VoiceRoom from '../components/VoiceRoom'
import toast from '../components/Toast'
import NotFoundPage from '../components/NotFoundPage'


export const logoPositionStrings = [
  'topRight',
  'topLeft',
  'topCenter',
  'leftCenter',
  'rightCenter',
  'bottomRight',
  'bottomLeft',
  'bottomCenter',
  'center'
] as const
export type LogoPosition = (typeof logoPositionStrings)[number]

export const textPositionStrings = [
  'left-1',
  'center-1',
  'right-1',
  'left-2',
  'center-2',
  'right-2',
  'left-3',
  'center-3',
  'right-3',
  'left-4',
  'center-4',
  'right-4',
  'left-5',
  'center-5',
  'right-5',
  'left-6',
  'center-6',
  'right-6',
  'left-7',
  'center-7',
  'right-7'
] as const
export type TextPosition = (typeof textPositionStrings)[number]

export type LandingDetails = {
  logo?: {
    position: LogoPosition
    img: string
  }
  text?: {
    position: TextPosition
    format: RawDraftContentState
  }
  background?: {
    img: string
    color: string
    layout: string
  }
  widget?: {
    widgetPosition: string

    principalColor: string
    secondaryColor: string

    ctaTimer: number
    triggerAfter: number
  }
}

export type Landing = {
  desktop?: LandingDetails
  mobile?: LandingDetails
  tablet?: LandingDetails
}

export const layoutStrings = ['desktop', 'mobile', 'tablet'] as const
export type Layout = (typeof layoutStrings)[number]

const modeStrings = ['viewer', 'builder'] as const
type Mode = (typeof modeStrings)[number]

const LandingPage = (): JSX.Element => {
  const location = useLocation()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const [pageConfig, setPageConfig] = useState<Landing | undefined>(undefined)
  const [activeLayout, setActiveLayout] = useState<Layout>('desktop')
  const [mode, setMode] = useState<Mode>('viewer')
  const [editorState, setEditorState] = useState<EditorState>()
  const [renderedText, setRenderedText] = useState<string>('')
  const [lineHeight, setLineHeight] = useState<number>(0)
  const [maxFontSize, setMaxFontSize] = useState<number>(0)
  const [language, setLanguage] = useState<string>('en')
  const [primaryColor, setPrimaryColor] = useState<string>('')
  const [secondaryColor, setSecondaryColor] = useState<string>('')
  const [showNotFound, setShowNotFound] = useState<boolean>(false)
  const id = queryParams.get('id')
  const v_zoom = queryParams.get('v_zoom')
  const active_layout = queryParams.get('active_layout')
  const preview = queryParams.has('widget-preview')
  const isStudioPreview = queryParams.has('mode') && queryParams.get('mode') === 'studio'

  useEffect((): (() => void) => {
    const handleResize = (): void => {
      // if mode is builder, we don't need to check the screen size
      if (mode !== 'builder') {
        if (window.innerHeight / window.innerWidth > 9.0 / 16.0) {
          setActiveLayout('mobile')
        } else if (window.innerHeight / window.innerWidth > 11.0 / 16.0) {
          setActiveLayout('tablet')
        } else {
          setActiveLayout('desktop')
        }
      } else {
        setActiveLayout(activeLayout ?? 'desktop')
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [activeLayout, mode])

  const callConfig = useCallback(async (): Promise<void> => {
    try {
      const mode = queryParams.get('mode')

      if (mode !== null && mode !== undefined && modeStrings.includes(mode as Mode)) {
        setMode(mode as Mode)
      }

      if (
        active_layout !== null &&
        active_layout !== undefined &&
        layoutStrings.includes(active_layout as Layout)
      ) {
        setActiveLayout(active_layout as Layout)
      }

      if (v_zoom !== null) {
        const zoomNumber =
          Number.parseFloat(v_zoom as string) >= 1 ? 1 : Number.parseFloat(v_zoom as string)
        if (zoomNumber && !Number.isNaN(zoomNumber)) {
          document.documentElement.style.setProperty('--page-scale', zoomNumber.toString())
        }
      }

      if (id !== null && id !== undefined) {
        const config = await api.getLanding(id, preview)
        // set in cookie language
        localStorage.setItem('locale', config.language)
        setPageConfig(config.landing)
        setLanguage(config.language)
        setPrimaryColor(config.primaryColor ?? '#FF0000')
        setSecondaryColor(config.secondaryColor ?? '#0000FF')
      }
    } catch (e) {
      console.log(`error ${e}`)
      setShowNotFound(true)
    }
  }, [active_layout, id, v_zoom, preview, queryParams])
  useEffect(() => {
    callConfig()
  }, [location.search, callConfig])
  useEffect(() => {
    if (pageConfig) {
      const mark = pageConfig[activeLayout]?.text?.format
      if (mark) {
        const convertedState = convertFromRaw(mark)
        const editorValue = EditorState.createWithContent(convertedState)

        setEditorState(editorValue)
      }
    }
  }, [pageConfig, activeLayout])

  const convertDescriptionFromJSONToHTML = useCallback(
    (state: ContentState): string => {
      const options: { inlineStyles: { [styleName: string]: RenderConfig } } = {
        inlineStyles: {
          SUPERSCRIPT: { element: 'sup' },
          SUBSCRIPT: { element: 'sub' },
          RED: { style: { color: '#900' } }
        }
      }

      const inlineStyleRanges = (value: RawDraftContentBlock): void => {
        value.inlineStyleRanges.forEach(v => {
          if (!(v.style in options.inlineStyles)) {
            let style_key = v.style.substring(0, v.style.indexOf('-'))
            if (v.style.includes('fontsize')) {
              const fontSizeString = v.style.split('-')[1]
              if (fontSizeString !== undefined) {
                const fs = parseInt(fontSizeString)

                if (fs > maxFontSize) {
                  setMaxFontSize(fs)
                }
              }
            }

            switch (style_key) {
              case 'fontsize':
                style_key = 'font-size'

                break

              case 'fontfamily':
                style_key = 'font-family'
                break
              default:
                break
            }

            // Concatenate line-height with existing style object
            const style_val = v.style.substring(v.style.indexOf('-') + 1)

            options.inlineStyles[v.style] = {
              style: {
                ...options.inlineStyles[v.style]?.style, // Existing styles
                [style_key]: style_val
                // "line-height": `${lineHeight}px`, // Line height with "px" unit
              }
            }
          }
        })
      }

      try {
        const raw = convertToRaw(state)
        if (raw.blocks.length > 0) {
          raw.blocks.forEach(inlineStyleRanges)
        }
        return stateToHTML(state, options)
      } catch (exp) {
        console.log(exp)
        toast.error('Conversion error')
        throw exp
      }
    },
    [maxFontSize]
  )

  useEffect(() => {
    if (editorState) {
      //const html = stateToHTML(editorState.getCurrentContent())
      try {
        const html = convertDescriptionFromJSONToHTML(editorState.getCurrentContent())
        setRenderedText(html)
      } catch {}
    }
  }, [editorState, maxFontSize, convertDescriptionFromJSONToHTML])

  useEffect(() => {
    if (maxFontSize < 11) {
      setLineHeight(0)
    } else if (maxFontSize >= 11 && maxFontSize < 20) {
      setLineHeight(Math.round(maxFontSize * 0.2))
    } else {
      setLineHeight(Math.round(maxFontSize * 0.8))
    }
  }, [maxFontSize])

  if (id === null || id === undefined || showNotFound) { // check if campaign product_url exist
    return <NotFoundPage locale={language} />
  }

  return (
    <div className="container">
      {pageConfig?.[activeLayout]?.background && !isStudioPreview && (
        <div
          className="background-container"
          style={{ backgroundColor: pageConfig[activeLayout]?.background?.color ?? 'white' }}
        >
          {pageConfig?.[activeLayout]?.background?.img?.endsWith(`.mp4`) ? (
            <video
              autoPlay
              loop
              muted
              className={`background-img-${pageConfig[activeLayout]?.background?.layout}`}
              playsInline
            >
              <source src={pageConfig[activeLayout]?.background?.img} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            pageConfig[activeLayout]?.background?.img && (
              <img
                className={`background-img-${pageConfig[activeLayout]?.background?.layout}`}
                src={pageConfig[activeLayout]?.background?.img}
                alt="Background"
              />
            )
          )}
        </div>
      )}
      <div className="assistant-container">
        <VoiceRoom
          activeLayout={activeLayout}
          language={language}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
        />
      </div>

      {pageConfig?.[activeLayout]?.logo && !isStudioPreview && (
        <div className={`logo-container logo-${pageConfig[activeLayout]?.logo?.position}`}>
          <img src={pageConfig[activeLayout]?.logo?.img} alt="Logo" />
        </div>
      )}
      {pageConfig?.[activeLayout]?.text && !isStudioPreview && (
        <span
          className={`text-layer text-${pageConfig[activeLayout]?.text?.position}`}
          style={{ lineHeight: `${lineHeight}px` }}
        >
          {pageConfig[activeLayout]?.text?.format !== undefined && (
            <div dangerouslySetInnerHTML={{ __html: renderedText }} />
          )}
        </span>
      )}
    </div>
  )
}

LandingPage.requireAuth = { shouldAuth: false }

export default LandingPage
